import { useContext, useEffect, useState } from 'react';
import { batch, useDispatch } from 'react-redux';

import { MediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import { useGuideInfo } from '@/hooks/app/useGuideInfo';
import { useThemeState } from '@/hooks/store';
import { setGuide } from '@/store/guide/actions';
import { Guide } from '@/store/guide/types';
import { setProgram } from '@/store/program/actions';
import { Program } from '@/store/program/types';
import { setTheme } from '@/store/theme/actions';

interface UseInitializeGuide {
	(guide: Guide, program: Program): {};
}

export const useInitializeGuide: UseInitializeGuide = (guide, program) => {
	const theme = useThemeState();
	const dispatch = useDispatch();
	const { image } = useGuideInfo(guide, program);
	const { onAudioStart, onVideoStart, playerMode } = useContext(MediaPlayerContext);

	const [hasStartedPlayback, setHasStartedPlayback] = useState(false);

	// All guides share one route, so this component instance is reused when the id changes —
	// a one-shot guard would leave the previous guide's backdrop on screen.
	// The comparison keeps that safe: `theme` is both a dependency here and part of the payload,
	// so an unconditional dispatch would re-enter this effect for as long as the payload keeps
	// differing — today the assets are stable store references, but comparing makes that
	// independent of asset-object identity.
	useEffect(() => {
		if (program && guide && theme.webAppBackgroundImage?.url !== image?.url) {
			dispatch(
				setTheme({
					...theme,
					webAppBackgroundImage: image,
				}),
			);
		}
	}, [dispatch, guide, image, program, theme]);

	// Playback is deliberately once-per-mount: `playerMode` is a dependency, so a
	// playing -> paused transition would otherwise re-dispatch and restart the track.
	useEffect(() => {
		const playContent = () => {
			if (playerMode !== 'playing' && program?.meditation_type !== 'timer') {
				batch(() => {
					dispatch(setGuide(guide));
					dispatch(setProgram(program));
				});

				if (guide.asset.type === 'audio') {
					onAudioStart(null, guide, program);
				} else if (guide.asset.type === 'video') {
					onVideoStart(guide, program, 'paused', false);
				}
			}
		};

		if (!hasStartedPlayback && program && guide) {
			playContent();
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasStartedPlayback(true);
		}
	}, [dispatch, guide, hasStartedPlayback, onAudioStart, onVideoStart, playerMode, program]);

	return {
		image,
	};
};
